import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  IconButton,
  InputAdornment,
  Chip,
  capitalize
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  AttachMoney as AttachMoneyIcon,
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarIcon,
  Calculate as CalculateIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Description as DescriptionIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { teacherService } from '../../services/api';
import { teacherPaymentService } from '../../services/teacherPaymentService';
import { uploadService } from '../../services/uploadService';
import dayjs from 'dayjs';
import DatePickerField from '../../components/DatePickerField';

const months = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const schema = yup.object({
  teacher_id: yup.number()
    .required('Debe seleccionar un profesor')
    .typeError('Debe seleccionar un profesor')
    .transform((value, originalValue) => {
      if (originalValue === '') return undefined;
      return Number(originalValue);
    }),
  month: yup.string().required('Debe seleccionar un mes'),
  year: yup.number()
    .required('El año es requerido')
    .min(2023, 'Año no válido')
    .max(2030, 'Año no válido'),
  hours_worked: yup.number()
    .required('Las horas son requeridas')
    .min(0, 'Las horas no pueden ser negativas')
    .typeError('Debe ser un número válido'),
  hourly_rate: yup.number()
    .required('La tarifa por hora es requerida')
    .min(0, 'La tarifa no puede ser negativa'),
  hours_extra: yup.number()
    .min(0, 'Las horas no pueden ser negativas')
    .typeError('Debe ser un número válido'),
  hourly_rate_extra: yup.number()
    .min(0, 'La tarifa no puede ser negativa'),
  total_amount: yup.number()
    .required('El total es requerido')
    .min(0, 'El total no puede ser negativo'),
  payment_date: yup.string()
    .required('La fecha de pago es requerida')
    .test('not-future', 'La fecha de pago no puede ser futura', (value) => {
      return dayjs(value).isSameOrBefore(dayjs(), 'day');
    }),
  status: yup.string().required('El estado es requerido'),
  description: yup.string().nullable(),
  bank_reference: yup.string().nullable(),
  payment_proof_url: yup.string().nullable() // 👈 Agregar campo para URL del comprobante
});

const TeacherPaymentForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const isEdit = !!id;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [teachers, setTeachers] = useState<any[]>([]);
  const [calculating, setCalculating] = useState(false);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [selectedTeacherData, setSelectedTeacherData] = useState<any>(null);
  
  // 👈 Estados para el archivo
  const [paymentFile, setPaymentFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<{
    name: string;
    url: string;
    type: string;
  } | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      teacher_id: undefined,
      month: capitalize(dayjs().format('MMMM')),
      year: dayjs().year(),
      hours_worked: 0,
      hourly_rate: 0,
      hours_extra: 0,
      hourly_rate_extra: 500,
      total_amount: 0,
      payment_date: dayjs().format('YYYY-MM-DD'),
      status: 'paid',
      description: '',
      bank_reference: '',
      payment_proof_url: ''
    }
  });

  const watchTeacherId = watch('teacher_id');
  const watchHours = watch('hours_worked');
  const watchRate = watch('hourly_rate');
  const watchHoursExtra = watch('hours_extra');
  const watchRateExtra = watch('hourly_rate_extra');
  const selectedTeacherId = watch('teacher_id');

  useEffect(() => {
    loadTeachers();
    
    if (isEdit) {
      loadPayment();
    }
  }, [id]);

  useEffect(() => {
    // Calcular total automáticamente
    const hours = watchHours || 0;
    const rate = watchRate || 0;
    const extraHours = watchHoursExtra || 0;
    const extraRate = watchRateExtra || 500;
    
    const total = (hours * rate) + (extraHours * extraRate);
    
    setValue('total_amount', Number(total.toFixed(2)));
  }, [watchHours, watchRate, watchHoursExtra, watchRateExtra, setValue]);

  useEffect(() => {
    const updateTeacherRate = async () => {
      if (!selectedTeacherId) {
        setValue('hourly_rate', 0);
        setSelectedTeacherData(null);
        return;
      }

      try {
        const teacher = teachers.find(t => t.id === selectedTeacherId);
        
        if (teacher) {
          setValue('hourly_rate', teacher.hourly_rate || 0);
          setSelectedTeacherData(teacher);
        } else {
          const response = await teacherService.getById(selectedTeacherId);
          const teacherData = response.data;
          setValue('hourly_rate', teacherData.hourly_rate || 0);
          setSelectedTeacherData(teacherData);
        }
      } catch (error) {
        console.error('Error loading teacher details:', error);
      }
    };

    updateTeacherRate();
  }, [selectedTeacherId, teachers, setValue]);

  // 👈 Cargar datos del pago si es edición
  const loadPayment = async () => {
    try {
      setLoading(true);
      const response = await teacherPaymentService.getById(parseInt(id!));
      const paymentData = response.data;
      
      // Si hay comprobante, cargar datos
      if (paymentData.payment_proof_url) {
        setUploadedFile({
          name: paymentData.payment_proof_url.split('/').pop() || 'comprobante',
          url: paymentData.payment_proof_url,
          type: 'application/pdf' // o detectar según extensión
        });
      }
      
      reset(paymentData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar pago');
    } finally {
      setLoading(false);
    }
  };

  const loadTeachers = async () => {
    try {
      setLoadingTeachers(true);
      const response = await teacherService.getAll({ per_page: 100, status: 'active' });
      setTeachers(response.data.data || []);
    } catch (err) {
      console.error('Error loading teachers:', err);
    } finally {
      setLoadingTeachers(false);
    }
  };

  // 👈 Función para manejar cambio de archivo
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        setError('Solo se permiten imágenes (JPEG, PNG, GIF) y archivos PDF');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        setError('El archivo no puede ser mayor a 5MB');
        return;
      }

      setPaymentFile(file);
      setError(null);
      
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFilePreview(e.target?.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setFilePreview(null);
      }
    }
  };

  // 👈 Función para eliminar archivo
  const handleFileRemove = () => {
    setPaymentFile(null);
    setFilePreview(null);
    setUploadedFile(null);
    setValue('payment_proof_url', '');
  };

  // 👈 Función para subir archivo al servidor
  const handleFileUpload = async (file: File): Promise<string> => {
    try {
      setUploadingFile(true);
      const response = await uploadService.uploadPaymentProof(file);
      
      setUploadedFile({
        name: file.name,
        url: response.data.file_url,
        type: file.type
      });
      
      setPaymentFile(null);
      setFilePreview(null);
      
      return response.data.file_url;
    } catch (err: any) {
      throw new Error(err.response?.data?.message || 'Error al subir archivo');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleCalculateSalary = async () => {
    if (!watchTeacherId || !watchHours || !watchHoursExtra || !watchRateExtra) {
      return;
    }

    try {
      setCalculating(true);
      const response = await teacherPaymentService.calculateSalary(watchTeacherId, {
        hours_worked: watchHours,
        hours_rate: watchRate,
        hours_extra: watchHoursExtra,
        hourly_rate_extra: watchRateExtra,
        month: watch('month'),
        year: watch('year')
      });
      
      setValue('hourly_rate', response.data.calculation.hourly_rate);
      setValue('total_amount', response.data.calculation.total_amount);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al calcular salario');
    } finally {
      setCalculating(false);
    }
  };

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);
      setError(null);

      let paymentProofUrl = data.payment_proof_url;

      // Subir archivo si hay uno pendiente
      if (paymentFile && !uploadedFile) {
        paymentProofUrl = await handleFileUpload(paymentFile);
      }

      const paymentData = {
        ...data,
        payment_proof_url: paymentProofUrl
      };

      if (isEdit) {
        await teacherPaymentService.update(parseInt(id!), paymentData);
      } else {
        await teacherPaymentService.create(paymentData);
      }

      setSuccess(true);
      
      setTimeout(() => {
        navigate('/teacher-payments');
      }, 2000);

    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al guardar pago');
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton component={Link} to="/teacher-payments" sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          {isEdit ? 'Editar Pago a Profesor' : 'Nuevo Pago a Profesor'}
        </Typography>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          ¡Pago guardado exitosamente! Redirigiendo...
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          {/* Selección de Profesor */}
          <Grid size={{ xs: 12 }}>
            <Controller
              name="teacher_id"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.teacher_id}>
                  <InputLabel>Profesor *</InputLabel>
                  <Select 
                    {...field} 
                    label="Profesor *"
                    disabled={loadingTeachers}
                  >
                    {loadingTeachers ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} /> Cargando...
                      </MenuItem>
                    ) : (
                      teachers.map((teacher) => (
                        <MenuItem key={teacher.id} value={teacher.id}>
                          <Box display="flex" alignItems="center">
                            <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                            <Box>
                              <Typography>
                                {teacher.first_name} {teacher.last_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {teacher.specialization} - ₽{teacher.hourly_rate}/hora
                              </Typography>
                            </Box>
                          </Box>
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {errors.teacher_id && (
                    <FormHelperText>{errors.teacher_id.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>

          {/* Período */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  2. Período
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="month"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.month}>
                          <InputLabel>Mes *</InputLabel>
                          <Select {...field} label="Mes *">
                            {months.map((month) => (
                              <MenuItem key={month} value={month}>{month}</MenuItem>
                            ))}
                          </Select>
                          {errors.month && (
                            <FormHelperText>{errors.month.message}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="year"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="number"
                          label="Año *"
                          error={!!errors.year}
                          helperText={errors.year?.message}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Datos del Pago */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  3. Datos del Pago
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Controller
                      name="hours_worked"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="number"
                          label="Horas Trabajadas *"
                          error={!!errors.hours_worked}
                          helperText={errors.hours_worked?.message}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <AccessTimeIcon />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <Button
                                  size="small"
                                  onClick={handleCalculateSalary}
                                  disabled={!watchTeacherId || !watchHours || calculating}
                                >
                                  {calculating ? <CircularProgress size={20} /> : 'Calcular'}
                                </Button>
                              </InputAdornment>
                            )
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Controller
                      name="hourly_rate"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="number"
                          label="Tarifa por Hora (RUB) *"
                          error={!!errors.hourly_rate}
                          helperText={errors.hourly_rate?.message}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <AttachMoneyIcon />
                              </InputAdornment>
                            )
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 3 }}>
                    <Controller
                      name="hours_extra"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="number"
                          label="Horas Extra"
                          error={!!errors.hours_extra}
                          helperText={errors.hours_extra?.message}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <AccessTimeIcon />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <Button
                                  size="small"
                                  onClick={handleCalculateSalary}
                                  disabled={!watchTeacherId || calculating}
                                >
                                  {calculating ? <CircularProgress size={20} /> : 'Calcular'}
                                </Button>
                              </InputAdornment>
                            )
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Controller
                      name="hourly_rate_extra"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="number"
                          label="Tarifa Extra (RUB)"
                          error={!!errors.hourly_rate_extra}
                          helperText={errors.hourly_rate_extra?.message}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <AttachMoneyIcon />
                              </InputAdornment>
                            )
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="total_amount"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="number"
                          label="Total a Pagar (RUB) *"
                          error={!!errors.total_amount}
                          helperText={errors.total_amount?.message}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <AttachMoneyIcon />
                              </InputAdornment>
                            )
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="payment_date"
                      control={control}
                      render={({ field }) => (
                        <DatePickerField
                          {...field}
                          label="Fecha de Ingreso"
                          error={!!errors.payment_date}
                          helperText={errors.payment_date?.message}
                          disabled={loading}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* 👈 NUEVA SECCIÓN: Comprobante de Pago */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  4. Comprobante de Pago
                </Typography>
                
                <input
                  type="file"
                  id="payment-proof-upload"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                  accept=".jpg,.jpeg,.png,.gif,.pdf"
                />
                
                {uploadedFile ? (
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Box display="flex" alignItems="center">
                      {uploadedFile.type.startsWith('image/') ? (
                        <Box
                          component="img"
                          src={uploadedFile.url}
                          alt="Comprobante"
                          sx={{
                            width: 80,
                            height: 80,
                            objectFit: 'cover',
                            borderRadius: 1,
                            mr: 2
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'grey.200',
                            borderRadius: 1,
                            mr: 2
                          }}
                        >
                          {uploadedFile.type === 'application/pdf' ? (
                            <PictureAsPdfIcon sx={{ fontSize: 40, color: 'error.main' }} />
                          ) : (
                            <ReceiptIcon sx={{ fontSize: 40, color: 'info.main' }} />
                          )}
                        </Box>
                      )}
                      
                      <Box flex={1}>
                        <Typography variant="subtitle2">
                          {uploadedFile.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Comprobante subido
                        </Typography>
                        <Box mt={1}>
                          <Chip
                            icon={<CheckCircleIcon />}
                            label="Archivo subido"
                            color="success"
                            size="small"
                          />
                        </Box>
                      </Box>
                      
                      <IconButton
                        color="error"
                        onClick={handleFileRemove}
                        disabled={uploadingFile}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Paper>
                ) : paymentFile ? (
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Box display="flex" alignItems="center">
                      {filePreview ? (
                        <Box
                          component="img"
                          src={filePreview}
                          alt="Preview"
                          sx={{
                            width: 80,
                            height: 80,
                            objectFit: 'cover',
                            borderRadius: 1,
                            mr: 2
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'grey.200',
                            borderRadius: 1,
                            mr: 2
                          }}
                        >
                          {paymentFile.type === 'application/pdf' ? (
                            <PictureAsPdfIcon sx={{ fontSize: 40, color: 'error.main' }} />
                          ) : (
                            <DescriptionIcon sx={{ fontSize: 40, color: 'info.main' }} />
                          )}
                        </Box>
                      )}
                      
                      <Box flex={1}>
                        <Typography variant="subtitle2">
                          {paymentFile.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(paymentFile.size / 1024).toFixed(1)} KB
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleFileUpload(paymentFile)}
                          disabled={uploadingFile}
                          sx={{ mr: 1 }}
                        >
                          {uploadingFile ? <CircularProgress size={20} /> : 'Subir'}
                        </Button>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={handleFileRemove}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </Paper>
                ) : (
                  <Paper
                    sx={{
                      border: '2px dashed',
                      borderColor: 'grey.300',
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      bgcolor: 'grey.50',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'primary.50'
                      }
                    }}
                    onClick={() => document.getElementById('payment-proof-upload')?.click()}
                  >
                    <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography>
                      Haz clic para seleccionar un comprobante
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Formatos: JPG, PNG, GIF, PDF (máx. 5MB)
                    </Typography>
                  </Paper>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Información Adicional */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  5. Información Adicional
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.status}>
                          <InputLabel>Estado *</InputLabel>
                          <Select {...field} label="Estado *">
                            <MenuItem value="paid">Pagado</MenuItem>
                            <MenuItem value="pending">Pendiente</MenuItem>
                            <MenuItem value="cancelled">Cancelado</MenuItem>
                          </Select>
                          {errors.status && (
                            <FormHelperText>{errors.status.message}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Controller
                      name="bank_reference"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Referencia Bancaria"
                          helperText="Número de referencia o comprobante del banco"
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Controller
                      name="description"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          multiline
                          rows={3}
                          label="Descripción"
                          placeholder="Información adicional sobre el pago..."
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Botones */}
          <Grid size={{ xs: 12 }}>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                onClick={() => navigate('/teacher-payments')}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading || success || uploadingFile}
              >
                {loading ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default TeacherPaymentForm;